// Collapsible section showing accommodation items per day with inline editing

import React, { useState } from "react";
import { useAccommodation } from "../../AccommodationProvider";
import { MiniDaySwitcher } from "../AssignedRooms/MiniDaySwitcher";
import { DiscountCell } from "../../../day-plan-fullscreen/components/PlanList/DiscountCell";
import { InlineEditCell } from "../../../day-plan-fullscreen/components/PlanList/InlineEditCell";
import { AddExtraDropdown } from "./AddExtraDropdown";
import type { DayPlanItem } from "../../../../types";

// Grid template matching spec: 1fr 80px 100px 80px 100px 40px
const GRID_TEMPLATE = "1fr 80px 100px 80px 100px 40px";

interface AccommodationItemRowProps {
  item: DayPlanItem;
  onFieldUpdate: (itemId: string, field: string, value: number) => Promise<void>;
  onDiscountChange: (itemId: string, updates: { discountType: "percentage" | "amount" | null; discountValue: number | null }) => Promise<void>;
  onDelete: (itemId: string) => Promise<void>;
}

const AccommodationItemRow: React.FC<AccommodationItemRowProps> = ({
  item,
  onFieldUpdate,
  onDiscountChange,
  onDelete,
}) => {
  const calculateTotal = () => {
    const base = item.quantity * item.unitPrice;
    if (!item.discountType || !item.discountValue) return base;
    if (item.discountType === "percentage") {
      return base * (1 - item.discountValue / 100);
    }
    if (item.discountType === "amount") {
      return Math.max(0, base - item.discountValue * item.quantity);
    }
    return base;
  };

  const handleFieldUpdate = async (field: string, value: number) => {
    await onFieldUpdate(item.id, field, value);
  };

  const handleDiscountChange = async (updates: {
    discountType: "percentage" | "amount" | null;
    discountValue: number | null;
  }) => {
    await onDiscountChange(item.id, updates);
  };

  const handleDelete = async () => {
    if (window.confirm("Czy na pewno chcesz usunąć tę pozycję?")) {
      await onDelete(item.id);
    }
  };

  const name =
    item.categoryOption?.name || item.customTitle || "Pozycja noclegowa";

  return (
    <div
      className="grid gap-2 py-2 px-4 bg-white border border-gray-200 hover:bg-gray-50 transition-colors items-center"
      style={{ gridTemplateColumns: GRID_TEMPLATE }}
    >
      {/* Element name */}
      <div className="text-sm font-medium text-gray-900 truncate">{name}</div>

      {/* Quantity */}
      <div className="w-20">
        <InlineEditCell
          value={item.quantity}
          type="number"
          onSave={(value: string) =>
            handleFieldUpdate("quantity", parseInt(value) || 1)
          }
          className="w-full text-xs text-center"
          inputProps={{ min: 1 }}
        />
      </div>

      {/* Unit Price */}
      <div className="w-25">
        <InlineEditCell
          value={item.unitPrice}
          type="number"
          onSave={(value: string) =>
            handleFieldUpdate("unitPrice", parseFloat(value) || 0)
          }
          className="w-full text-xs text-center"
          inputProps={{ min: 0, step: 0.01 }}
          suffix="zł"
        />
      </div>

      {/* Discount */}
      <div className="w-20">
        <DiscountCell
          discountType={item.discountType}
          discountValue={item.discountValue}
          quantity={item.quantity}
          unitPrice={item.unitPrice}
          onDiscountChange={handleDiscountChange}
          className="w-full"
        />
      </div>

      {/* Total */}
      <div className="text-sm font-medium text-gray-900 text-right">
        {calculateTotal().toFixed(2)} zł
      </div>

      {/* Actions */}
      <div className="flex items-center justify-center">
        <button
          onClick={handleDelete}
          className="text-red-500 hover:text-red-700 text-xs"
          title="Usuń pozycję"
          aria-label={`Usuń pozycję ${name}`}
        >
          ✕
        </button>
      </div>
    </div>
  );
};

/**
 * Collapsible section showing accommodation plan items for the selected day.
 * Allows inline editing of quantity, unit price, and discount.
 * Also provides "Dodaj dostawkę" dropdown for adding extra accommodation options.
 *
 * State sync: mutations go through AccommodationProvider methods which handle
 * API calls and refreshing the shared state (accommodationDays + assignedRooms).
 */
export const AccommodationDetailSection: React.FC = () => {
  const {
    offerId,
    currentDayIndex,
    accommodationDays,
    setCurrentDay,
    updateAccommodationItem,
    deleteAccommodationItem,
    addToAllDays,
    toggleAddToAllDays,
  } = useAccommodation();

  const [isExpanded, setIsExpanded] = useState(true);
  const [showAddExtra, setShowAddExtra] = useState(false);

  // Get accommodation items for the selected day
  const currentDay = accommodationDays[currentDayIndex];
  const accommodationItems = (currentDay?.planItems || []).filter(
    (item: DayPlanItem) => item.categoryOption?.optionType === "accommodation"
  );

  const handleFieldUpdate = async (itemId: string, field: string, value: number) => {
    await updateAccommodationItem(currentDay.id, itemId, { [field]: value } as Partial<Pick<DayPlanItem, 'unitPrice' | 'quantity' | 'discountType' | 'discountValue'>>);
  };

  const handleDiscountChange = async (itemId: string, updates: { discountType: "percentage" | "amount" | null; discountValue: number | null }) => {
    await updateAccommodationItem(currentDay.id, itemId, updates as Partial<Pick<DayPlanItem, 'unitPrice' | 'quantity' | 'discountType' | 'discountValue'>>);
  };

  const handleDelete = async (itemId: string) => {
    await deleteAccommodationItem(currentDay.id, itemId);
  };

  const handleExtraAdded = async () => {
    setShowAddExtra(false);
  };

  const sectionId = "accommodation-detail-section";

  return (
    <div className="bg-white rounded-lg shadow mt-6">
      {/* Collapsible header */}
      <button
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors rounded-lg"
        aria-expanded={isExpanded}
        aria-controls={sectionId}
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <h3 className="text-lg font-medium text-gray-900">
          Szczegóły noclegów
        </h3>
        <span className="text-gray-500 text-sm">
          {isExpanded ? "▲" : "▼"}
        </span>
      </button>

      {/* Expandable content */}
      {isExpanded && (
        <div id={sectionId} className="border-t border-gray-200">
          {/* Mini Day Switcher */}
          <div className="px-4 py-3 border-b border-gray-100">
            <MiniDaySwitcher
              days={accommodationDays}
              currentDayIndex={currentDayIndex}
              onDayChange={setCurrentDay}
            />
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <div className="min-w-full">
              {/* Table headers */}
              <div
                className="grid gap-2 py-3 px-4 bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-700"
                style={{ gridTemplateColumns: GRID_TEMPLATE }}
              >
                <div>Element</div>
                <div className="text-center">Ilość</div>
                <div className="text-center">Cena jedn.</div>
                <div className="text-center">Rabat</div>
                <div className="text-right">Suma</div>
                <div></div>
              </div>

              {/* Items or empty state */}
              {accommodationItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Brak noclegów na ten dzień</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {accommodationItems.map((item: DayPlanItem) => (
                    <AccommodationItemRow
                      key={item.id}
                      item={item}
                      onFieldUpdate={handleFieldUpdate}
                      onDiscountChange={handleDiscountChange}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Add extra dropdown */}
          <div className="px-4 py-3 border-t border-gray-100">
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={addToAllDays}
                onChange={toggleAddToAllDays}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                data-testid="detail-add-to-all-days"
              />
              <span className="text-sm text-gray-700">Dodaj na wszystkie dni</span>
            </label>
            {showAddExtra ? (
              <AddExtraDropdown
                offerId={offerId}
                dayId={currentDay?.id}
                existingItemCategoryOptionIds={accommodationItems
                  .filter((item: DayPlanItem) => item.categoryOption?.id)
                  .map((item: DayPlanItem) => item.categoryOption!.id)}
                onClose={() => setShowAddExtra(false)}
                onAdded={handleExtraAdded}
              />
            ) : (
              <button
                onClick={() => setShowAddExtra(true)}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                + Dodaj
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
